import { Alert, Button, Card, Checkbox, Divider, Form, Input, Typography } from 'antd';
import { FingerPrintIcon, LockPasswordIcon, Mail01Icon } from '@hugeicons/core-free-icons';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/index.js';
import { ApiError } from '@/lib/api/index.js';
import { neutral, space, typography } from '@/design/tokens.js';
import { useLogin, type LoginRequest } from './auth.api.js';
import { PasswordExpiredPage } from './PasswordExpiredPage.js';
import { MfaChallengePage } from './MfaChallengePage.js';
import {
  cancelPasskeyCeremony,
  PasskeyCancelledError,
  PasskeyUnavailableError,
  supportsPasskeyAutofill,
  supportsPasskeys,
  usePasskeyLogin,
} from './passkey.api.js';

const { Text } = Typography;

/**
 * Sign-in.
 *
 * **Passkey first, password second** (ADR 0005). The passkey button sits above
 * the form and is the visually primary action; the password form is below a
 * divider that names it as the alternative. Where the browser supports
 * conditional UI, passkeys are also offered straight from the email field's
 * autofill, so the fastest path needs no button at all.
 *
 * The passkey button is hidden entirely on a browser without WebAuthn, rather
 * than shown and failing — offering an action that cannot work is the error
 * we are trying to prevent.
 *
 * Error handling switches on the server's stable `code`, never on message
 * text, and each code gets the specific guidance that actually helps:
 *
 *   - wrong password  → try again, with the field still filled in
 *   - locked out      → say how long, because only waiting fixes it
 *   - inactive        → tell them to contact HR, since they cannot fix it
 *
 * A generic "login failed" for all three is the error handling Nielsen's
 * heuristics warn against: it names the symptom and hides the remedy.
 */
export function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const passkeyLogin = usePasskeyLogin();
  const [form] = Form.useForm<LoginRequest>();
  const [notice, setNotice] = useState<{ type: 'error' | 'warning'; text: string } | null>(null);
  const [passkeysAvailable] = useState(supportsPasskeys);
  /** Set when the password was right but has expired; carries the email over. */
  const [expiredFor, setExpiredFor] = useState<string | null>(null);
  /** Set when the password was right but a second factor is still needed. */
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);

  /** Where the guard wanted to send them before redirecting here. */
  const intended = (location.state as { from?: string } | null)?.from ?? '/';

  /** Explicit button press. The user asked, so failures are reported. */
  const signInWithPasskey = async () => {
    setNotice(null);
    try {
      await passkeyLogin.mutateAsync({ useAutofill: false });
      navigate(intended, { replace: true });
    } catch (error) {
      // Dismissing the platform prompt is a decision, not a failure. Showing
      // "authentication failed" for it would be both wrong and alarming.
      if (error instanceof PasskeyCancelledError) return;
      setNotice({ type: 'error', text: explainPasskey(error) });
    }
  };

  /**
   * Conditional UI: offer passkeys from the email field's autofill, with no
   * button pressed.
   *
   * This is an opportunistic background offer the user never asked for, so it
   * **fails silently, always**. Surfacing "that passkey could not be used" to
   * someone who has just loaded the page and touched nothing is alarming and
   * meaningless — and it happens routinely, because browsers abort the
   * conditional ceremony for their own reasons.
   *
   * Only the explicit button reports errors.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (!(await supportsPasskeyAutofill())) return;
        if (cancelled) return;
        await passkeyLogin.mutateAsync({ useAutofill: true });
        if (!cancelled) navigate(intended, { replace: true });
      } catch {
        // Intentionally swallowed. See above.
      }
    })();

    return () => {
      cancelled = true;
      // The browser allows only one ceremony at a time, so an abandoned
      // conditional one would block the next and resolve into a component
      // that no longer exists.
      cancelPasskeyCeremony();
    };
    // Deliberately once on mount: restarting the ceremony on every render
    // would cancel the one the user is part-way through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFinish = async (values: LoginRequest) => {
    setNotice(null);
    try {
      const result = await login.mutateAsync(values);

      switch (result.status) {
        case 'AUTHENTICATED':
          navigate(intended, { replace: true });
          return;
        case 'PASSWORD_EXPIRED':
          // Not a failure — the credentials were right. Hand them straight to
          // the screen that finishes the job, rather than an error they then
          // have to work out how to act on.
          setExpiredFor(values.email);
          return;
        case 'MFA_REQUIRED':
          setMfaChallengeId(result.challengeId);
          return;
      }
    } catch (error) {
      setNotice({ type: 'error', text: explain(error) });

      // Clear only the password. Making someone retype their email address
      // after a typo in the password is gratuitous.
      form.setFieldValue('password', '');
    }
  };

  if (mfaChallengeId) {
    return (
      <MfaChallengePage challengeId={mfaChallengeId} onCancel={() => setMfaChallengeId(null)} />
    );
  }

  if (expiredFor) {
    return <PasswordExpiredPage email={expiredFor} onCancel={() => setExpiredFor(null)} />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: space[4],
        background: 'var(--ant-color-bg-layout)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: space[6] }}>
          <span
            aria-hidden
            style={{
              display: 'inline-grid',
              placeItems: 'center',
              width: 44,
              height: 44,
              borderRadius: 11,
              background:
                'linear-gradient(135deg, var(--ant-color-primary) 0%, var(--ant-color-primary-active) 100%)',
              color: neutral[0],
              fontWeight: typography.weight.bold,
              fontSize: typography.size.xl,
              marginBottom: space[3],
            }}
          >
            O
          </span>
          <div
            style={{
              fontSize: typography.size.xl,
              fontWeight: typography.weight.semibold,
              letterSpacing: typography.letterSpacing.tighter,
            }}
          >
            Innowaft Orbit
          </div>
          <Text type="secondary">Sign in with your work account</Text>
        </div>

        <Card variant="outlined">
          {passkeysAvailable ? (
            <>
              <Button
                type="primary"
                size="large"
                block
                icon={<Icon icon={FingerPrintIcon} size="md" />}
                loading={passkeyLogin.isPending}
                onClick={() => void signInWithPasskey()}
                style={{ marginBottom: space[2] }}
              >
                Sign in with a passkey
              </Button>
              <Text
                type="secondary"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  fontSize: typography.size.sm,
                }}
              >
                Faster, and it cannot be phished.
              </Text>
              <Divider style={{ marginBlock: space[4], fontSize: typography.size.sm }} plain>
                or use your password
              </Divider>
            </>
          ) : null}

          {notice ? (
            <Alert
              type={notice.type}
              showIcon
              title={notice.text}
              style={{ marginBottom: space[4] }}
            />
          ) : null}

          <Form<LoginRequest>
            form={form}
            layout="vertical"
            requiredMark={false}
            initialValues={{ email: '', password: '', rememberDevice: false }}
            onFinish={(values) => void onFinish(values)}
            disabled={login.isPending}
          >
            <Form.Item
              name="email"
              label="Work email"
              rules={[
                { required: true, message: 'Enter your work email address' },
                { type: 'email', message: 'Enter a valid email address' },
              ]}
            >
              <Input
                size="large"
                autoFocus
                // `username` (not `email`) is what password managers and, later,
                // passkey conditional UI look for.
                autoComplete="username"
                inputMode="email"
                placeholder="you@innowaft.com"
                prefix={<Icon icon={Mail01Icon} size="sm" />}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Enter your password' }]}
            >
              <Input.Password
                size="large"
                autoComplete="current-password"
                placeholder="••••••••"
                prefix={<Icon icon={LockPasswordIcon} size="sm" />}
              />
            </Form.Item>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: space[2],
                marginBottom: space[4],
              }}
            >
              <Form.Item name="rememberDevice" valuePropName="checked" noStyle>
                <Checkbox>Keep me signed in</Checkbox>
              </Form.Item>
              <Link to="/forgot-password" style={{ fontSize: typography.size.sm }}>
                Forgot password?
              </Link>
            </div>

            <Button
              // Secondary when a passkey is possible: there should be exactly
              // one primary action, and it is the phishing-resistant one.
              type={passkeysAvailable ? 'default' : 'primary'}
              htmlType="submit"
              size="large"
              block
              loading={login.isPending}
            >
              {passkeysAvailable ? 'Sign in with password' : 'Sign in'}
            </Button>
          </Form>
        </Card>

        <Text
          type="secondary"
          style={{
            display: 'block',
            textAlign: 'center',
            marginTop: space[4],
            fontSize: typography.size.sm,
          }}
        >
          Trouble signing in? Contact your HR administrator.
        </Text>
      </div>
    </div>
  );
}

/**
 * Turns a failure into advice.
 *
 * Switches on the stable error code, never on the message text, so rewording a
 * server message can never silently change which branch runs here.
 */
function explainPasskey(error: unknown): string {
  // The browser refused outright rather than the passkey being rejected. Its
  // own wording says what to do, and a generic "could not be used" here would
  // send somebody looking for a passkey problem they do not have.
  if (error instanceof PasskeyUnavailableError) return error.message;
  if (!ApiError.is(error)) {
    return 'That passkey could not be used. Try again, or sign in with your password.';
  }
  switch (error.code) {
    case 'INVALID_CREDENTIALS':
      return 'That passkey is not recognised here. Sign in with your password instead.';
    case 'ACCOUNT_INACTIVE':
      return 'This account is not active. Contact HR for help.';
    case 'STATE_CONFLICT':
      return 'That took too long. Please try again.';
    default:
      return error.message;
  }
}

function explain(error: unknown): string {
  if (!ApiError.is(error)) {
    return 'We could not reach the server. Check your connection and try again.';
  }

  switch (error.code) {
    case 'INVALID_CREDENTIALS':
      return 'That email and password combination is not correct.';
    case 'ACCOUNT_LOCKED':
      // The server's message carries the actual wait, which is the one piece
      // of information that makes this error actionable.
      return error.message;
    case 'ACCOUNT_INACTIVE':
      return 'This account is not active. Contact HR for help.';
    case 'VALIDATION_FAILED':
      return error.issues[0]?.message ?? 'Check the details and try again.';
    case 'RATE_LIMITED':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return error.message;
  }
}
